/* eslint-disable react/jsx-filename-extension, global-require */
import React, { lazy } from 'react'
import _get from 'lodash/get'
import _concat from 'lodash/concat'
import _isFunction from 'lodash/isFunction'
import { get as getConfig } from 'config'
import { isProduction } from 'environment'
import {
  ERROR_TYPE_403,
  ERROR_TYPE_404,
  ERROR_TYPE_500,
  ERROR_TYPE_503,
} from 'components/ErrorPage/types'
import Sherpa, { TYPE_LARGE } from 'components/Sherpa'
import {
  URL_HOME,
  URL_LOGOUT,
  URL_REFER,
  URL_REFER_JOIN,
  URL_ACCOUNT_SETTINGS,
  URL_ACCOUNT_CANCEL_CONFIRM,
  URL_ACCOUNT_CANCEL_PAUSE,
  URL_ACCOUNT_CANCEL_OFFER,
  URL_ACCOUNT_CANCEL_SPECIAL_OFFER,
  URL_ACCOUNT_PROFILE,
  URL_ACCOUNT_CANCEL,
  URL_ACCOUNT_CREATION,
  URL_CART_ACCOUNT_CREATION_CREATE,
  URL_EMAIL_SETTINGS,
  URL_GIFT_REVIEW,
  URL_GIFT_REDEEM,
  URL_GIFT_PAYMENT,
  URL_GIFT_COMPLETE,
  URL_CART_ACCESS_DENIED,
  URL_ACCOUNT_PAUSE,
  URL_ACCOUNT_PAUSE_CONFIRM,
  URL_ACCOUNT_INVOICE,
  URL_AMBASSADORS,
  URL_PLAN_SELECTION,
  URL_PLAN_SELECTION_PLANS,
  URL_CART_BILLING,
  URL_CART_BILLING_PAYMENT,
  URL_ACCOUNT_UPDATE_PAYMENT,
  URL_GAIA_PLUS_EVENTS,
  URL_GAIA_PLUS_ALL_GUIDES,
  URL_REMOTE_ACCOUNT_CREATE,
  URL_YOGA_TEACHERS,
  URL_CHECKOUT,
  URL_ACCOUNT_CANCEL_FREE_MONTH,
  URL_LOGIN_WITH_LINK,
  URL_MANAGE_PROFILES,
  URL_AI_CHAT,
  URL_AMA_EVENTS,
} from 'services/url/constants'
import {
  RESOLVER_TYPE_NOT_FOUND,
  RESOLVER_TYPE_PORTAL,
  RESOLVER_TYPE_SHARE,
  RESOLVER_TYPE_EVENTS,
  RESOLVER_TYPE_PLAYLIST,
  RESOLVER_TYPE_GIFT,
} from 'services/resolver/types'
import SearchPageSwitch from 'components/SearchPageSwitch/SearchPageSwitch'

const LoadableYogaHome = lazy(
  () => import('components/YogaHome'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableIndexPage = lazy(
  () => import('components/IndexPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableNotificationsCenterAll = lazy(
  () => import('components/NotificationsCenterAll'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableVideoPage = lazy(
  () => import('components/VideoPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableDetailPageV2 = lazy(
  () => import('components/DetailPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableDetailPageV3 = lazy(
  () => import('components/DetailPageV3'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableSharePage = lazy(
  () => import('components/SharePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableOnboardingPage = lazy(
  () => import('components/Onboarding/OnboardingStart'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableResetPage = lazy(
  () => import('components/ResetPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableEventDetailsPage = lazy(
  () => import('components/EventDetailsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableEventsPage = lazy(
  () => import('components/EventsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGaiaPlusPage = lazy(
  () => import('components/GaiaPlusPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableLivePage = lazy(
  () => import('components/LivePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAMAPage = lazy(
  () => import('components/AMAPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableChatPopOutPage = lazy(
  () => import('components/LivePage/ChatPopOutPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableLiveChannelPage = lazy(
  () => import('components/LiveChannelPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAccountPausePage = lazy(
  () => import('components/AccountPause/AccountPausePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAccountPauseConfirmPage = lazy(
  () => import('components/AccountPause/AccountPauseConfirmPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAccountCancelPage = lazy(
  () => import('components/AccountCancelV2/AccountCancelPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCancelConfirmPage = lazy(
  () => import('components/AccountCancelV2/AccountCancelConfirmPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCancelOfferPage = lazy(
  () => import('components/AccountCancelV2/AccountCancelOfferPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCancelSpecialOffer = lazy(
  () => import('components/AccountCancelV2/AccountCancelSpecialOffer'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableFreeMonthPage = lazy(
  () => import('components/AccountCancelFreeMonthPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountChangePlanPage = lazy(
  () => import('components/MyAccount/MyAccountChangePlanPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountInvoicePage = lazy(
  () => import('components/MyAccount/MyAccountInvoicePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableTopics = lazy(
  () => import('components/TopicsPageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyLibraryPage = lazy(
  () => import('components/MyLibraryPage/MyLibraryPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCommunityMyLibraryPage = lazy(
  () => import('components/Community/Identity/Library/CommunityMyLibraryPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableOraclePage = lazy(
  () => import('components/OraclePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMultiplePlaylists = lazy(
  () => import('components/MultiplePlaylists/MultiplePlaylistsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadablePlaylistAnonymousPage = lazy(
  () => import('components/PlaylistAnonymousPage/PlaylistAnonymousPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableActivate = lazy(
  () => import('components/ActivateRemoteLoginCodePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableShopifyMultipass = lazy(
  () => import('components/ShopifyMultipassLoginPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGo = lazy(
  () => import('components/GoPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGoV2 = lazy(
  () => import('components/GoPage.v2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableRemoteAccountCreatePage = lazy(
  () => import('components/RemoteAccountCreatePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartAccessDeniedPage = lazy(
  () => import('components/CartAccessDeniedPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartPlansPageV2 = lazy(
  () => import('components/CartV2/CartPlansPageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartSinglePage = lazy(
  () => import('components/CartSinglePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartChoosePlanV2 = lazy(
  () => import('components/CartV2/CartChoosePlanPageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartAccountCreateV2 = lazy(
  () => import('components/CartV2/CartAccountCreatePageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAccountCreationV2 = lazy(
  () => import('components/CartV2/CartAccountContinuePageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartPaymentPageV2 = lazy(
  () => import('components/CartV2/CartPaymentPageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartChoosePaymentV2 = lazy(
  () => import('components/CartV2/CartChoosePaymentPageV2'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableCartConfirmationV2 = lazy(
  () => import('components/CartV2/CartConfirmationPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableInviteFriendPage = lazy(
  () => import('components/InviteFriend/InviteFriendPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableInviteFriendReferralsPage = lazy(
  () => import('components/InviteFriend/InviteFriendReferralsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountProfilePage = lazy(
  () => import('components/MyAccount/MyAccountProfilePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableManageProfiles = lazy(
  () => import('components/ManageProfilesPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountPage = lazy(
  () => import('components/MyAccount/MyAccountPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountSettings = lazy(
  () => import('components/MyAccount/MyAccountSettings'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMyAccountUpdatePaymentPage = lazy(
  () => import('components/MyAccount/MyAccountUpdatePaymentPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableRecentlyAdded = lazy(
  () => import('components/RecentlyAddedPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableSystemPlaylistFullView = lazy(
  () => import('components/Community/Identity/Library/SystemPlaylistFullView'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableLogoutPage = lazy(
  () => import('components/LogoutPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableKitchenSink = lazy(
  () => import('components/KitchenSink'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableYogaTeachersPage = lazy(
  () => import('components/YogaTeachersPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableMemberPortal = lazy(
  () => import('components/Portal'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadablePortalsPage = lazy(
  () => import('components/Portal/PortalsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableSubcategoryPage = lazy(
  () => import('components/SubcategoryPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadablePolicyPage = lazy(
  () => import('components/PolicyPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableErrorPage = lazy(
  () => import('components/ErrorPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableNotificationsCenterManage = lazy(
  () => import('components/NotificationsCenterManage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableEmailPreferencesPage = lazy(
  () => import('components/EmailPreferencesPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGiftRedeemPage = lazy(
  () => import('components/Gift/GiftRedeemPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGiftReviewPage = lazy(
  () => import('components/Gift/GiftReviewPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGiftPaymentPage = lazy(
  () => import('components/Gift/GiftPaymentPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGiftCreatePage = lazy(
  () => import('components/Gift/GiftCreatePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGiftCompletePage = lazy(
  () => import('components/Gift/GiftCompletePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableGuidePage = lazy(
  () => import('components/GuidedPrograms/Guide'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableAmbassadorsPage = lazy(
  () => import('components/Ambassadors/AmbassadorsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
// adyen-test-page
const LoadableAdyenTestPage = lazy(
  () => import('components/AdyenTestPage/AdyenTestPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableEventsAMAPage = lazy(
  () => import('components/EventsAMAPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)
const LoadableLoginWithLinkPage = lazy(
  () => import('components/LoginWithLinkPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

const LoadableCommunityProfilePage = lazy(
  () => import('components/Community/Identity/Profile/CommunityProfilePage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

const LoadableCommunityProfileSettingsPage = lazy(
  () => import('components/Community/Identity/Settings/CommunityProfileSettingsPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

const LoadablePlaylistSharedPage = lazy(
  () => import('components/Community/Playlist/SharedPage/PlaylistSharedPage'),
  { fallback: <Sherpa type={TYPE_LARGE} /> },
)

function renderVideoRouteComponent (props) {
  const query = _get(props.location, 'query', {})
  if (query.fullplayer && !getConfig().features.videoDetailInlinePlayer) {
    return <LoadableVideoPage
      location={props.location}
      history={props.history}
      apolloClient={props.apolloClient}
    />
  }

  return (
    <LoadableDetailPageV2
      location={props.location}
      history={props.history}
      apolloClient={props.apolloClient}
    />
  )
}

function renderPlaylistPageComponent (props) {
  const query = _get(props.location, 'query', {})
  if (query.playlistCreate && query.contentIds) {
    return <LoadablePlaylistAnonymousPage history={props.history} location={props.location} />
  }
  const config = getConfig()
  if (config.features.communityProfile) {
    return <LoadableCommunityMyLibraryPage history={props.history} location={props.location} />
  }
  if (config.features.myLibraryPageGraphQL) {
    return <LoadableMyLibraryPage history={props.history} location={props.location} />
  }
  return <LoadableMultiplePlaylists history={props.history} location={props.location} />
}

// Zuora Outage - ac-8105
const renderZuoraOutageCheckoutComponent = (props) => {
  if (getConfig().features.zuoraOutage) {
    return <LoadableCartChoosePlanV2 history={props.history} location={props.location} />
  }

  return null
}

const firstRoutes = [
  {
    path: '/share/:token',
    resolverType: RESOLVER_TYPE_SHARE,
    component: (props) => {
      return <LoadableSharePage location={props.location} history={props.history} />
    },
  },
  {
    path: '/portal/:uid',
    resolverType: RESOLVER_TYPE_PORTAL,
    component: (props) => {
      return <LoadableMemberPortal location={props.location} history={props.history} />
    },
  },
  {
    path: '/portals',
    component: (props) => {
      return (
        <LoadablePortalsPage location={props.location} history={props.history} />
      )
    },
  },
  {
    path: '/get-started',
    component: (props) => {
      return <LoadableOnboardingPage location={props.location} history={props.history} />
    },
  },
  {
    path: '/password-reset',
    component: (props) => {
      return <LoadableResetPage location={props.location} history={props.history} />
    },
  },
  {
    path: '/show/:id', // @todo: how to handle multiple paths that use same component?
    component: renderVideoRouteComponent,
  },
  {
    path: '/tv/:id',
    component: renderVideoRouteComponent,
  },
  {
    path: '/series/:id',
    component: renderVideoRouteComponent,
  },
  {
    path: '/video/:id',
    component: renderVideoRouteComponent,
  },
  {
    path: '/course/:id',
    component: (props) => {
      if (getConfig().features.courseDetailPage) {
        const query = _get(props.location, 'query', {})
        if (query.fullplayer) {
          return <LoadableVideoPage
            location={props.location}
            history={props.history}
            apolloClient={props.apolloClient}
          />
        }
        return <LoadableDetailPageV3 history={props.history} location={props.location} />
      }
      return <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_404} />
    },
  },
  {
    path: '/gaia-plus/:id',
    resolverType: RESOLVER_TYPE_EVENTS,
    component: (props) => {
      const pathname = _get(props.location, 'pathname')
      if (pathname === URL_GAIA_PLUS_ALL_GUIDES
        || pathname === URL_GAIA_PLUS_EVENTS) {
        return (
          <LoadableSubcategoryPage
            history={props.history}
            location={props.location}
            defaultSort="popular"
          />
        )
      }
      return <LoadableEventDetailsPage location={props.location} history={props.history} />
    },
  },
  {
    path: '/gaia-plus',
    component: (props) => {
      if (getConfig().features.gaiaPlusPage) {
        return <LoadableGaiaPlusPage history={props.history} location={props.location} />
      }

      return <LoadableEventsPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_AMA_EVENTS,
    component: (props) => {
      return <LoadableEventsAMAPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/live',
    component: (props) => {
      return <LoadableLivePage history={props.history} location={props.location} eventType="LIVE" />
    },
  },
  {
    path: '/ama',
    component: (props) => {
      return <LoadableAMAPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/ama/chat-popout',
    component: (props) => {
      return <LoadableChatPopOutPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/gaia-now',
    component: (props) => {
      return <LoadableLiveChannelPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_PAUSE,
    component: (props) => {
      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableAccountPausePage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_PAUSE_CONFIRM,
    component: (props) => {
      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableAccountPauseConfirmPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_CANCEL_CONFIRM,
    component: (props) => {
      if (!getConfig().features.userAccount.cancelV2) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableCancelConfirmPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_CANCEL_OFFER,
    component: (props) => {
      if (!getConfig().features.userAccount.cancelV2) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableCancelOfferPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_CANCEL_SPECIAL_OFFER,
    component: (props) => {
      return <LoadableCancelSpecialOffer history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_CANCEL_FREE_MONTH,
    component: (props) => {
      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableFreeMonthPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/account/change-plan',
    component: (props) => {
      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }
      return (
        <LoadableMyAccountChangePlanPage history={props.history} location={props.location} />
      )
    },
  },
  {
    path: URL_ACCOUNT_INVOICE,
    component: (props) => {
      return (
        <LoadableMyAccountInvoicePage history={props.history} location={props.location} />
      )
    },
  },
  {
    path: URL_EMAIL_SETTINGS,
    component: (props) => {
      return <LoadableEmailPreferencesPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/topics',
    component: (props) => {
      return <LoadableTopics history={props.history} location={props.location} />
    },
  },
  {
    path: '/search',
    component: (props) => {
      return <SearchPageSwitch history={props.history} location={props.location} />
    },
  },
  {
    path: '/playlist/shared/:playlistId',
    component: (props) => {
      return <LoadablePlaylistSharedPage history={props.history} location={props.location} />
    },
  },
  {
    // Custom playlist owner's view — UUID-constrained so slug paths (default,
    // favorites, bonus-*, purchases-*) continue to match /playlist/:playlistType.
    // Reuses SystemPlaylistFullView, which branches on pathname.
    path: '/playlist/:playlistId([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})',
    component: (props) => {
      const config = getConfig()
      if (!config.features.communityProfile) {
        return <LoadableErrorPage
          history={props.history}
          location={props.location}
          code={ERROR_TYPE_404} />
      }
      return <LoadableSystemPlaylistFullView history={props.history} location={props.location} />
    },
  },
  {
    path: '/playlist/:playlistType',
    resolverType: RESOLVER_TYPE_PLAYLIST,
    component: renderPlaylistPageComponent,
  },
  {
    path: '/activate',
    component: (props) => {
      return <LoadableActivate history={props.history} location={props.location} />
    },
  },
  {
    path: '/mp-login',
    component: (props) => {
      return <LoadableShopifyMultipass history={props.history} location={props.location} />
    },
  },
  {
    path: '/go',
    component: (props) => {
      if (getConfig().features.goPageV2) {
        return <LoadableGoV2 history={props.history} location={props.location} />
      }
      return <LoadableGo history={props.history} location={props.location} />
    },
  },
  {
    path: URL_REMOTE_ACCOUNT_CREATE,
    component: (props) => {
      return <LoadableRemoteAccountCreatePage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_CART_ACCESS_DENIED,
    component: (props) => {
      return <LoadableCartAccessDeniedPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_PLAN_SELECTION_PLANS,
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableCartPlansPageV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_CHECKOUT,
    component: (props) => {
      // single page checkout toggle
      if (getConfig().features.singlePageCheckout) {
        return <LoadableCartSinglePage history={props.history} location={props.location} />
      }

      return (
        <LoadableErrorPage
          history={props.history}
          location={props.location}
          code={ERROR_TYPE_404}
        />
      )
    },
  },
  {
    path: URL_PLAN_SELECTION,
    component: (props) => {
      return <LoadableCartChoosePlanV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_CART_ACCOUNT_CREATION_CREATE,
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableCartAccountCreateV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_CREATION,
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableAccountCreationV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_CART_BILLING_PAYMENT,
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableCartPaymentPageV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_CART_BILLING,
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableCartChoosePaymentV2 history={props.history} location={props.location} />
    },
  },
  {
    path: '/cart/confirmation',
    component: (props) => {
      // Zuora Outage - ac-8105
      const zuoraOutageComponent = renderZuoraOutageCheckoutComponent(props)
      if (zuoraOutageComponent) return zuoraOutageComponent

      return <LoadableCartConfirmationV2 history={props.history} location={props.location} />
    },
  },
  {
    path: URL_MANAGE_PROFILES,
    component: (props) => {
      return <LoadableManageProfiles history={props.history} location={props.location} />
    },
  },
  {
    path: '/account',
    component: (props) => {
      return <LoadableMyAccountPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_SETTINGS,
    component: (props) => {
      return <LoadableMyAccountSettings history={props.history} location={props.location} />
    },
  },
  {
    path: URL_ACCOUNT_UPDATE_PAYMENT,
    component: (props) => {
      return (
        <LoadableMyAccountUpdatePaymentPage
          history={props.history}
          location={props.location}
        />
      )
    },
  },
  {
    path: URL_ACCOUNT_CANCEL,
    component: (props) => {
      if (!getConfig().features.userAccount.cancelV2) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      // Zuora Outage - ac-8105
      if (getConfig().features.zuoraOutage) {
        return (
          <LoadableMyAccountPage history={props.history} location={props.location} />
        )
      }

      return <LoadableAccountCancelPage history={props.history} location={props.location} />
    },
  },
  //
  {
    path: URL_ACCOUNT_CANCEL_PAUSE,
    component: (props) => {
      return (
        <LoadableAccountPausePage location={props.location} history={props.history} fromCancel />
      )
    },
  },
  //
  {
    path: URL_ACCOUNT_PROFILE,
    component: (props) => {
      return <LoadableMyAccountProfilePage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_REFER_JOIN,
    component: (props) => {
      return (
        <LoadableInviteFriendPage location={props.location} history={props.history} />
      )
    },
  },
  {
    path: URL_REFER,
    component: (props) => {
      return (
        <LoadableInviteFriendReferralsPage history={props.history} location={props.location} />
      )
    },
  },
  {
    path: '/terms-privacy',
    component: (props) => {
      return <LoadablePolicyPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/recently-added',
    component: (props) => {
      return <LoadableRecentlyAdded history={props.history} location={props.location} />
    },
  },
  {
    path: '/guide/:id',
    component: (props) => {
      return <LoadableGuidePage location={props.location} history={props.history} />
    },
  },
  {
    path: '/watch-history',
    component: (props) => {
      return <LoadableSystemPlaylistFullView history={props.history} location={props.location} />
    },
  },
  {
    path: '/watch-later',
    component: (props) => {
      return <LoadableSystemPlaylistFullView history={props.history} location={props.location} />
    },
  },
  {
    path: '/favorites',
    component: (props) => {
      return <LoadableSystemPlaylistFullView history={props.history} location={props.location} />
    },
  },
  {
    path: '/currently-watching',
    component: (props) => {
      return <LoadableSystemPlaylistFullView history={props.history} location={props.location} />
    },
  },
  {
    path: '/profile/edit',
    component: (props) => (
      <LoadableCommunityProfileSettingsPage
        history={props.history}
        location={props.location}
      />
    ),
  },
  {
    path: '/profile/:uid',
    component: (props) => (
      <LoadableCommunityProfilePage
        history={props.history}
        location={props.location}
      />
    ),
  },
  {
    path: URL_AI_CHAT,
    component: (props) => {
      if (getConfig().features?.ai?.oracle) {
        return <LoadableOraclePage location={props.location} history={props.history}/>
      }
      return (
        <LoadableErrorPage
          history={props.history}
          location={props.location}
          code={ERROR_TYPE_404}
        />
      )
    },
  },
  {
    path: URL_LOGOUT,
    component: (props) => {
      return <LoadableLogoutPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/dev/kitchen-sink',
    component: (props) => {
      if (!getConfig().features.development.kitchenSink) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }
      return <LoadableKitchenSink history={props.history} location={props.location} />
    },
  },
  {
    path: URL_YOGA_TEACHERS,
    component: (props) => {
      return <LoadableYogaTeachersPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_GIFT_COMPLETE,
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      if (!getConfig().features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }
      return <LoadableGiftCompletePage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_GIFT_REDEEM,
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      if (!getConfig().features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }
      return <LoadableGiftRedeemPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_GIFT_REVIEW,
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      if (!getConfig().features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      return <LoadableGiftReviewPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_GIFT_PAYMENT,
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      if (!getConfig().features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      return <LoadableGiftPaymentPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/gift/:sku',
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      if (!getConfig().features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }

      return <LoadableGiftCreatePage history={props.history} location={props.location} />
    },
  },
  {
    path: '/gift',
    resolverType: RESOLVER_TYPE_GIFT,
    component: (props) => {
      const config = getConfig()
      if (!config.features.gift.checkout) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }
      const { location = {} } = global
      // I really hate doing a location change this way but I have no choice.
      // There are a few problems with our current gift implementation.
      //
      // 1. part of the experience is in Wordpress while the other is in WebApp.
      // This creates a cross-site depenency that is a pain in the but to manage,
      // especially in different environments (ie: local, stage, qa, prod) and if/when urls changes
      //
      // 2. Because there is no Wordpress server for localhost or qa, we need to hard-code our stage
      // environment here if we want the full experience. It's crappy. We also do not have our
      // environment origins defined in config() anywhere so I have to hard code the domain here.
      //
      // 3. Originally we tried to do a location.href = '/gift' because we have Cloudfront setup
      // to re-route /gift -> /lp/give-the-gift-of-gaia but that does not seem to be working...
      const root = isProduction() ? '' : 'https://www-stg-4b2c19.gaia.com'
      // we need to do a true location change so that we hit the server instead
      // of just navigating to /gift through React router.
      if (_isFunction(location.replace)) {
        location.replace(`${root}/give-the-gift-of-gaia`)
      }
      return null
    },
  },
  {
    path: URL_AMBASSADORS,
    component: (props) => {
      if (!getConfig().features.ambassadorPayouts) {
        return (
          <LoadableErrorPage
            history={props.history}
            location={props.location}
            code={ERROR_TYPE_404}
          />
        )
      }
      return <LoadableAmbassadorsPage history={props.history} location={props.location} />
    },
  },
  {
    path: '/dev/adyen', // adyen-test-page
    component: (props) => {
      return <LoadableAdyenTestPage history={props.history} location={props.location} />
    },
  },
  {
    path: URL_LOGIN_WITH_LINK,
    component: (props) => {
      if (getConfig().features.loginWithCode) {
        return <LoadableLoginWithLinkPage history={props.history} location={props.location} />
      }
      return <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_404}
      />
    },
  },
]

const shareViewRoutes = [
  {
    path: '/notifications/manage',
    component: (props) => (
      <LoadableNotificationsCenterManage
        history={props.history}
        location={props.location}
      />
    ),
  },
  {
    path: '/notifications',
    component: (props) => (
      <LoadableNotificationsCenterAll
        history={props.history}
        location={props.location}
      />
    ),
  },
]

const lastRoutes = [
  {
    path: '/error/403',
    // This is an error so we want the app to render
    // the error page, but not resolve the path.
    resolverType: RESOLVER_TYPE_NOT_FOUND,
    component: (props) => (
      <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_403}
      />
    ),
  },
  {
    path: '/error/404',
    // This is an error so we want the app to render
    // the error page, but not resolve the path.
    resolverType: RESOLVER_TYPE_NOT_FOUND,
    component: (props) => (
      <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_404}
      />
    ),
  },
  {
    path: '/error/500',
    // This is an error so we want the app to render
    // the error page, but not resolve the path.
    resolverType: RESOLVER_TYPE_NOT_FOUND,
    component: (props) => (
      <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_500}
      />
    ),
  },
  {
    path: '/error/503',
    // This is an error so we want the app to render
    // the error page, but not resolve the path.
    resolverType: RESOLVER_TYPE_NOT_FOUND,
    component: (props) => (
      <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_503}
      />
    ),
  },
  {
    path: '/:category/:subCategory',
    component: (props) => {
      const { location } = props
      const isYogaPractices = (_get(location, 'pathname') === '/yoga/practices')
      const isYogaMeditation = (_get(location, 'pathname') === '/style/yoga-meditation')
      let selectedFilterSet = _get(location, ['query', 'filter-set'])
      if (isYogaMeditation) {
        selectedFilterSet = 'yoga'
      }
      const isYogaFiltered = (selectedFilterSet === 'yoga')
      const isYoga = isYogaPractices || isYogaFiltered
      const defaultSort = isYoga ? 'recent' : 'popular'

      return (
        <LoadableSubcategoryPage
          history={props.history}
          location={props.location}
          defaultSort={defaultSort}
        />
      )
    },
  },
  {
    path: '/yoga',
    component: (props) => {
      return (
        <LoadableYogaHome
          history={props.history}
          location={props.location}
        />
      )
    },
  },
  {
    path: URL_HOME,
    component: (props) => {
      return <LoadableIndexPage history={props.history} location={props.location} />
    },
  },
  {
    path: '*',
    // This is a catch all error so we want the app to render
    // the error page, which is current the 404 page.
    component: (props) => (
      <LoadableErrorPage
        history={props.history}
        location={props.location}
        code={ERROR_TYPE_404}
      />
    ),
  },
]

const middleRoutes = getConfig().features.shareView ? shareViewRoutes : []
export default _concat(firstRoutes, middleRoutes, lastRoutes)
